import Hotel from "@/Hotels";
import {Tour} from "@/Tour";

export abstract class IFilter {

    public name: string;
    public key: string;
    public symbol = "";

    protected constructor(name: string, key: string) {
        this.name = name;
        this.key = key;
    }

    abstract initialize(hotels: Hotel[]): boolean;
    abstract test(hotel: Hotel): boolean;
    abstract to_string(): string;
    abstract from_string(value: string): void;
    abstract changed(): boolean;
    abstract reset(): void;

    setSymbol(symbol: string){
        this.symbol = symbol;
    }
}

export class RangeFilter extends IFilter{

    private min: number;
    private max: number;
    public values: [number,number] = [0,0];
    public type = "range";

    constructor(name: string,key: string) {
        super(name,key);
        this.max = 0;
        this.min = 9999999;
    }

    reset() {
        this.values = [this.min,this.max];
    }

    from_string(value: string) {
        const values = value.split('-');
        this.values[0] = +values[0];
        this.values[1] = +values[1];
    }

    changed(): boolean {
        return this.max !== this.values[1] || this.min !== this.values[0];
    }

    to_string(): string {
        return `${this.values[0]}-${this.values[1]}`;
    }

    initialize(hotels: Hotel[]) {
        let min = 9999999;
        let max = 0;

        hotels.forEach(hotel=>{

            if(hotel.tours.length <= 0){
                return;
            }

            const min_tour = hotel.tours.reduce((prev: Tour,current: Tour)=>{
                return prev[this.key as keyof Tour] > current[this.key as keyof Tour] ? current : prev;
            },hotel.tours[0])[this.key as keyof Tour] as number;

            const max_tour = hotel.tours.reduce((prev: Tour,current: Tour)=>{
                return prev[this.key as keyof Tour] < current[this.key as keyof Tour] ? current : prev;
            },hotel.tours[0])[this.key as keyof Tour] as number;

            if(min_tour){
                min = Math.min(min_tour,min);
            }
            if(max_tour){
                max = Math.max(max_tour,max);
            }
        })

        if(min !== 9999999 && max !== 0){
            this.max = max;
            this.min = min;
            this.values = [min,max];
            return true;
        }
        return false;
    }

    test(hotel: Hotel): boolean {

        for(const tour of hotel.tours){
            const value = (tour as any)[this.key];

            if(!this.changed()){
                return true;
            }

            if(value === undefined || value <= this.values[1] && value >= this.values[0]){
                return true;
            }
        }

        return false;
    }
}

export class SelectFilter<TKey extends keyof Hotel> extends IFilter {

    public values: {[key:string]:any};
    public checked: string[];
    public successes: {[key:string]: number};
    public type = "select";

    to_string(): string {
        return this.checked.join(',');
    }

    from_string(value: string) {
        this.checked = value.split(',');
    }

    changed(): boolean {
        return this.checked.length !== 0;
    }

    constructor(name: string,key: TKey,values: {[key:string]:any}) {
        super(name,key);
        this.values = values;
        this.checked = [];
        this.successes = {};
    }

    reset() {
        this.checked = [];
    }

    initialize(): boolean {
        return true;
    }

    test(hotel: Hotel): boolean {
        const key: string = this.key;


        if(key in hotel){
            const value = hotel[key as keyof Hotel];

            if(value instanceof Array){

                const findIndex = value.findIndex(value1 => this.checked.includes(value1.toString()));
                if(findIndex !== -1) {
                    this.addSuccess(value[findIndex].toString());
                    return true;
                }

                if(this.checked.length === 0){
                    value.forEach(valItem=>{
                        this.addSuccess(valItem.toString());
                    })
                    return true;
                }
                return false;
            }

            if(this.checked.length === 0 || this.checked.includes(value.toString())){
                if(value){
                    this.addSuccess(value.toString());
                }
                return true;
            }
            return false;
        }

        for(const tour of hotel.tours){
            const value = tour[key as keyof Tour];

            if(this.checked.length !== 0 && (value === undefined || value === null)){
                continue;
            }

            if(this.checked.length === 0){
                value && this.addSuccess(value.toString());
                return true;
            }

            if(value && this.checked.includes(value.toString())){
                this.addSuccess(value.toString());
                return true;
            }
        }

        return false;
    }

    public resetSuccesses(){
        this.successes = {};
    }

    private addSuccess(value: string){
        if(this.successes[value.toString()]){
            this.successes[value.toString()]++;
        }else{
            this.successes[value.toString()] = 1;
        }
    }
}

export class StarsFilter extends IFilter{

    public max: number;
    public min: number;
    public values: [number,number];
    public type = "stars_range"

    constructor(name: string,key: string,max: number,min: number) {
        super(name,key);
        this.max = max;
        this.min = min;
        this.values = [min,max];
    }

    reset() {
        this.values = [this.min,this.max];
    }

    to_string(): string {
        return `${this.values[0]}-${this.values[1]}`;
    }

    from_string(value: string) {
        const values = value.split('-');
        this.values[0] = +values[0];
        this.values[1] = +values[1];
    }

    initialize(): boolean {
        return true;
    }

    changed(): boolean {
        return this.max !== this.values[1] || this.min !== this.values[0];
    }

    test(hotel: Hotel): boolean {
        for(const tour of hotel.tours){
            const value = tour[this.key as keyof Tour];

            if(this.max === this.values[1] && this.min === this.values[0]){
                return true;
            }

            if(value === undefined || value <= this.values[1] && value >= this.values[0]){
                return true;
            }
        }

        return false;
    }
}